import { apiPrefix } from 'src/settings';
import { logError, logInfo } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { AssetType, SceneService_AssetType, SceneService_AssetType_NotSaved } from 'src/types/asset';
import { getSdk } from 'src/sdk';
import type { AssetItemEntity } from '@rendering/web-client/dist/types/modules';

const BASE_URL = `https://${apiPrefix}assets.documents.cimpress.io/v3/assets`;

/** Generates a URI to a specific version of an asset */
export function generateVersionContentUri({ assetId, versionId }: { assetId: string; versionId: string }) {
    if (!assetId || !versionId) {
        return undefined;
    }
    return `${BASE_URL}/${assetId}/revisions/${versionId}/content`;
}

/** Loads an asset by url. */
export function loadAssetUri({ assetId, versionId }: { assetId: string; versionId: string }) {
    return getSdk()
        .Assets.getRevisionVersionContent(assetId, versionId, { 'Cache-Control': 'no-cache' })
        .then((res) => res)
        .catch((e) => {
            logError(`Load Asset error (assetId=${assetId} version=${versionId}): ${parseError(e)}`);
            throw e;
        });
}

// Helper to parse an asset
export function parseAsset(asset: SceneService_AssetType): AssetType {
    return {
        latestVersionId: asset.latestRevisionId,
        publishedVersionId: asset.publishedRevisionId,
        id: asset.id,
        name: asset.name,
        mcpSku: asset.referenceId,
        mcpSkuVersion: asset.referenceIdVersion,
        description: asset.description,
        notes: asset.notes,
        created: asset.created,
        owner: asset.owner,
        lastModified: asset.lastModified,
        modifiedBy: asset.modifiedBy,
        lastPublished: asset.lastPublished,
        publishedBy: asset.publishedBy,
        variables: asset.attributes || {},
        tenantType: asset.tenant.type,
        tenantId: asset.tenant.id,
        deleted: asset.deleted || false,
        tags: asset.tags || [],
        publishedVersions: (asset.recentPublishedRevisions || []).map((version) => ({
            id: version.revisionId,
            date: version.publishedAt,
            publisher: version.publishedBy,
        })),
    };
}

// Helper to map SDK response to SceneService_AssetType
function mapSdkToSceneService_AssetType(response: AssetItemEntity): SceneService_AssetType {
    return {
        ...response,
        tags: response.tags || [],
        lastModified: response.lastModified ? new Date(response.lastModified) : undefined,
        lastPublished: response.lastPublished ? new Date(response.lastPublished) : undefined,
        recentPublishedRevisions:
            response.recentPublishedRevisions?.map((version) => ({
                publishedAt: new Date(version.publishedAt),
                publishedBy: version.publishedBy || '',
                revisionId: version.revisionId || '',
            })) || [],
        initialRevision: response.initialRevision
            ? {
                  uri: response.initialRevision.uri || '',
                  metadata: response.initialRevision.metadata || {},
              }
            : undefined,
    };
}

/**
 * Creates an asset
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller)
 * @param options.tenantId - The id of the resource
 * @param options.asset - The updated asset object
 * @param options.asset.assetType - The type of the asset
 * @param options.asset.name - The updated asset name
 * @param options.asset.description - The updated asset description
 * @param options.asset.referenceId - The updated sku
 * @param options.asset.attributes - The updated sku variables
 * @param options.asset.tags - string array of tags for the asset
 * @param options.asset.initialRevision - the initial version of this asset
 * @param options.asset.initialRevision.uri - the URI to the initial version's content
 * @param [options.asset.initialRevision.metadata] - the metadata for this type of asset
 * @returns The promise of the request.
 */
export function createAsset({
    tenantType,
    tenantId,
    asset,
}: {
    tenantType: string;
    tenantId: string;
    asset: Omit<SceneService_AssetType_NotSaved, 'tenant'>;
}): Promise<AssetType> {
    const data = {
        ...asset,
        tenant: {
            id: tenantId,
            type: tenantType,
        },
    };
    return getSdk()
        .Assets.create(data)
        .then((response) => {
            logInfo(`Created asset: ${response} ${JSON.stringify(response || {})}`);
            return parseAsset(mapSdkToSceneService_AssetType(response));
        })
        .catch((e) => {
            logError(`Creating asset failed (data=${JSON.stringify(data)}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Updates an asset
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller)
 * @param options.tenantId - The id of the resource
 * @param options.assetId - The id of the asset
 * @param options.newAsset - The updated asset object
 * @param options.newAsset.assetType - The type of the asset
 * @param options.newAsset.name - The updated asset name
 * @param options.newAsset.description - The updated asset description
 * @param options.newAsset.referenceId - The updated sku
 * @param options.newAsset.attributes - The updated sku variables
 * @param options.newAsset.latestRevisionId - The updated latest version
 * @param options.newAsset.publishedRevisionId - The updated published version
 * @param options.newAsset.tags - string array of tags for the asset
 * @param options.newAsset.deleted - if the asset is deleted or not
 * @returns The promise of the request
 */
export function updateAsset({
    tenantType,
    tenantId,
    assetId,
    newAsset,
}: {
    tenantType: string;
    tenantId: string;
    assetId: string;
    newAsset: {
        assetType?: string;
        name?: string;
        description?: string;
        referenceId?: string;
        referenceIdVersion?: number;
        attributes?: Record<string, any>;
        latestRevisionId?: string;
        publishedRevisionId?: string;
        tags?: string[];
        deleted?: boolean;
        notes?: string;
    };
}) {
    const data = {
        ...newAsset,
        tenant: {
            id: tenantId,
            type: tenantType,
        },
    };
    return getSdk()
        .Assets.update(assetId, data)
        .catch((e) => {
            logError(`Updating asset failed (asset=${assetId}) (data=${JSON.stringify(data)}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Creates an asset version
 * @param options
 * @param options.assetId - The id of the asset
 * @param options.version - The asset version object
 * @param options.version.uri - The uri to the new version's content
 * @param [options.version.metadata] - metadata for this type of asset
 * @returns The promise of the request.
 */
export function createVersion({
    assetId,
    version,
}: {
    assetId: string;
    version: { uri: string; metadata?: Record<string, any> };
}) {
    return getSdk()
        .Assets.createRevision(assetId, version.uri, version.metadata)
        .catch((e) => {
            logError(
                `Creating asset version failed (asset=${assetId}) (data=${JSON.stringify(version)}): ${parseError(e)}`,
            );
            throw e;
        });
}

/**
 * Get a specific version of a specific asset
 * @param options
 * @param options.assetId - The id of the asset
 * @param options.versionId - The id of the version
 * @returns The promise of the request
 */
export function getVersion({ assetId, versionId }: { assetId: string; versionId: string }): Promise<any> {
    return getSdk()
        .Assets.getRevisionVersion(assetId, versionId, { 'Cache-Control': 'no-cache' })
        .then((res) => res)
        .catch((e) => {
            logError(`Load versioned asset failed (asset=${assetId} version=${versionId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Gets an asset
 * @param options
 * @param options.assetId - The id of the asset
 * @returns The promise of the request
 */
export function getAsset({ assetId }: { assetId: string }) {
    return getSdk()
        .Assets.get(assetId, { 'Cache-Control': 'no-cache' })
        .then((res) => parseAsset(mapSdkToSceneService_AssetType(res)))
        .catch((e) => {
            logError(`Getting asset failed (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Delete an asset
 * @param options
 * @param options.assetId - The ID of the asset
 */
export function deleteAsset({ assetId }: { assetId: string }) {
    return getSdk()
        .Assets.remove(assetId)
        .catch((e) => {
            logError(`Deleting asset failed (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Restore an asset
 * @param options
 * @param options.assetId - The ID of the asset
 */
export function restoreAsset({ assetId }: { assetId: string }) {
    return getSdk()
        .Assets.restore(assetId)
        .catch((e) => {
            logError(`Restoring asset failed (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}
